/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Anchor, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { simpleIssueCard_versionIssueAssociatedDesigns$key } from '@atlassian/jira-relay/src/__generated__/simpleIssueCard_versionIssueAssociatedDesigns.graphql';
import { useSidebarStore } from '@atlassian/jira-software-releases-issue-sidebar-use-sidebar-store/src/controllers/index.tsx';
import IssueTypeAvatarFallbackUrl from './assets/issue-type-avatar-fallback.svg';

type Props = {
	jiraIssue: simpleIssueCard_versionIssueAssociatedDesigns$key;
	openIssueInSidebar?: boolean;
};

interface IssueTypeFieldData {
	icon: string;
	name: string;
}

export const SimpleIssueCard = ({ jiraIssue, openIssueInSidebar }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, sidebarActions] = useSidebarStore();

	const data = useFragment(
		graphql`
			fragment simpleIssueCard_versionIssueAssociatedDesigns on JiraIssue {
				key @required(action: THROW)
				webUrl @required(action: THROW)
				fieldsByIdOrAlias(idsOrAliases: ["issuetype", "summary"], ignoreMissingFields: true)
					@optIn(to: "JiraIssueFieldsByIdOrAlias")
					@required(action: THROW) {
					__typename
					... on JiraIssueTypeField {
						issueType @required(action: THROW) {
							name @required(action: THROW)
							avatar {
								xsmall
							}
						}
					}
					... on JiraSingleLineTextField {
						text @required(action: THROW)
					}
				}
			}
		`,
		jiraIssue,
	);

	const fieldsData = data.fieldsByIdOrAlias.filter(Boolean);
	const issueTypeFieldData = fieldsData.reduce<IssueTypeFieldData>(
		(acc, fieldData) => {
			if (fieldData.__typename === 'JiraIssueTypeField') {
				return {
					icon: fieldData.issueType.avatar?.xsmall ?? IssueTypeAvatarFallbackUrl,
					name: fieldData.issueType.name,
				};
			}

			return acc;
		},
		{
			icon: IssueTypeAvatarFallbackUrl,
			name: '',
		},
	);
	const summaryText = fieldsData.reduce<string | null>((acc, fieldData) => {
		if (fieldData.__typename === 'JiraSingleLineTextField') {
			return fieldData.text;
		}

		return acc;
	}, null);

	if (issueTypeFieldData == null) {
		throw new Error(`issue data did not contain issuetype data(${issueTypeFieldData})`);
	}
	if (summaryText === null) {
		throw new Error('issue data did not contain summary data');
	}

	return (
		<Anchor
			href={data.webUrl}
			target="_blank"
			xcss={wrapperStyles}
			onClick={(e) => {
				const modifiersPressed = e.shiftKey || e.metaKey || e.ctrlKey;
				// Open the issue in the sidebar if (a) the prop says we should and (b) the user
				// didn't *explicitly* try to open in a new tab, e.g. by control-clicking.
				if (openIssueInSidebar && !modifiersPressed) {
					e.preventDefault();
					sidebarActions.showIssue(data.key);
				}
				fireUIAnalytics(createAnalyticsEvent({}), 'link clicked', 'designLinkedIssue');
			}}
		>
			<Inline xcss={inlineStyles}>
				<img
					css={issueTypeStyles}
					src={issueTypeFieldData.icon}
					alt={issueTypeFieldData.name}
					title={issueTypeFieldData.name}
				/>
				<span css={issueKeyStyles}>{data.key}</span>
				<span css={issueSummaryStyles}>{summaryText}</span>
			</Inline>
		</Anchor>
	);
};

const wrapperStyles = xcss({
	textDecoration: 'none',
	boxSizing: 'content-box',
	display: 'block',
	height: '20px',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	':hover': {
		textDecoration: 'none',
	},
});

const inlineStyles = xcss({
	alignItems: 'center',
});

const issueTypeStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 2}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 2}px`,
	marginTop: 0,
	marginRight: token('space.100'),
	marginBottom: 0,
	marginLeft: 0,
	flex: '0 0 auto',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const issueKeyStyles = css({
	flexShrink: 0,
	color: token('color.text.subtlest'),
	paddingRight: token('space.300'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		textDecoration: 'none',
	},
});

const issueSummaryStyles = css({
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	color: token('color.text'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		textDecoration: 'underline',
	},
});
